import type { Program } from "@hybrid-iptv/types";
import { normalize } from "../search/useUniversalSearch.js";

/**
 * Pure scan for Section 12 notifications (steps 6 + 8) — no browser, no
 * store access, fully unit-testable.
 *
 * Two alert kinds, both over "program starts within the window and hasn't
 * started yet":
 * - `starting-soon`: the program's channel is favorited.
 * - `tracked-title`: the program title matches a tracked title.
 * A program can produce both (different dedupe keys, both fire).
 */
export type AlertKind = "starting-soon" | "tracked-title";

export interface AiringAlert {
  kind: AlertKind;
  title: string;
  channelName: string;
  startsAt: string;
  /** Stable per-alert key — persisted so a reload never re-fires it. */
  dedupeKey: string;
  message: string;
}

export interface ScanInput {
  /** Programs already mapped to OUR channel ids (mirrors useProgramIndex). */
  programs: Program[];
  /** Our channel id → display name (for the notification body). */
  channelNameById: ReadonlyMap<string, string>;
  /** Channel ids the user favorited. */
  favoriteChannelIds: ReadonlySet<string>;
  /** Raw tracked titles (normalized internally). */
  trackedTitles: string[];
  now: Date;
  windowMs: number;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "soon";
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m} ${ampm}`;
}

export function scanForNotifications(input: ScanInput): AiringAlert[] {
  const { programs, channelNameById, favoriteChannelIds, trackedTitles, now, windowMs } = input;
  const nowMs = now.getTime();
  const horizon = nowMs + windowMs;
  const tracked = trackedTitles
    .map((raw) => ({ raw, key: normalize(raw) }))
    .filter((t) => t.key.length > 0);

  const alerts: AiringAlert[] = [];
  for (const p of programs) {
    const startMs = new Date(p.start).getTime();
    if (Number.isNaN(startMs) || startMs <= nowMs || startMs > horizon) continue;

    const channelName = channelNameById.get(p.channelId) ?? "a channel";

    if (favoriteChannelIds.has(p.channelId)) {
      alerts.push({
        kind: "starting-soon",
        title: p.title,
        channelName,
        startsAt: p.start,
        dedupeKey: `soon:${p.channelId}:${p.start}`,
        message: `${p.title} starts at ${formatTime(p.start)} on ${channelName}`,
      });
    }

    const normTitle = normalize(p.title);
    for (const t of tracked) {
      // Substring both ways: a tracked "The Office" matches an EPG title
      // "The Office: The Dundies", and a tracked short title matches a longer
      // program title (normalize strips punctuation, so case/accents/symbols
      // already don't matter).
      if (normTitle.includes(t.key) || t.key.includes(normTitle)) {
        alerts.push({
          kind: "tracked-title",
          title: p.title,
          channelName,
          startsAt: p.start,
          dedupeKey: `tracked:${t.key}:${p.channelId}:${p.start}`,
          message: `“${p.title}” airs at ${formatTime(p.start)} on ${channelName}`,
        });
        break;
      }
    }
  }
  return alerts;
}
